import { useState, useCallback } from 'react'
import { useAuthStore } from 'shared/store/auth'
import {
	installDevOverrideSession,
	clearAuthSession,
} from 'shared/lib/auth/session'
import { mobileTrpcClient } from 'shared/api/core/trpc'
import { queryClient } from 'shared/api/core/tanstack-query-config'

// ---------------------------------------------------------------------------
// Preset users — hardcoded list of key test accounts for quick switching.
// ---------------------------------------------------------------------------

interface PresetUser {
	label: string
	description: string
	identifier: string
}

const PRESET_USERS: PresetUser[] = [
	{
		label: '👑 Super Admin',
		description: '',
		identifier: '744079466',
	},
	{
		label: '💰 Finance Admin',
		description: '',
		identifier: '6a54bdd079dcf150a2449338',
	},
	{
		label: '📦 Lots Admin',
		description: '',
		identifier: '998',
	},
	{
		label: '🛒 Test Buyer',
		description: '',
		identifier: '7508112796',
	},
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AccountSwitcher() {
	const user = useAuthStore((s) => s.user)
	const mode = useAuthStore((s) => s.mode)

	const [backendUnlocked, setBackendUnlocked] = useState(() => hasDevCookie())
	const [backendPassword, setBackendPassword] = useState('')
	const [customIdentifier, setCustomIdentifier] = useState('')
	const [loading, setLoading] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	// ---- Backend unlock ---------------------------------------------------

	const unlockBackend = useCallback(async () => {
		setLoading('unlock')
		setError(null)
		try {
			const res = await mobileTrpcClient.dev.Unlock.mutate({
				password: backendPassword,
			})
			document.cookie = `dev_mode=${res.token}; Path=/; max-age=31536000; SameSite=Lax`
			setBackendUnlocked(true)
			setBackendPassword('')
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Unlock failed')
		} finally {
			setLoading(null)
		}
	}, [backendPassword])

	// ---- Login as user ----------------------------------------------------

	const loginAs = useCallback(async (identifier: string) => {
		setLoading(identifier)
		setError(null)
		try {
			const result = await mobileTrpcClient.dev.Login.mutate({ identifier })
			queryClient.clear()
			installDevOverrideSession(result)
		} catch (err) {
			if (isDevTokenRejected(err)) {
				// The token was rotated server-side: the cookie is still here but no
				// longer valid, so drop it and bring the unlock form back instead of
				// leaving every switch failing against a lock that looks open.
				clearDevCookie()
				setBackendUnlocked(false)
				setError('Dev token no longer valid — unlock again')
			} else {
				setError(err instanceof Error ? err.message : 'Login failed')
			}
		} finally {
			setLoading(null)
		}
	}, [])

	// ---- Reset to original session ----------------------------------------

	const resetToTelegram = useCallback(() => {
		// Remove the dev override — next reload will re-authenticate via Telegram initData.
		localStorage.removeItem('@auth:telegram:v3')
		clearAuthSession('telegram')
		window.location.reload()
	}, [])

	return (
		<div className='space-y-4'>
			<h4 className='text-sm font-semibold uppercase tracking-wider text-[var(--tgui--hint_color,#999)]'>
				Account Switcher
			</h4>

			{/* ── Backend lock status ─────────────────────────────── */}
			{!backendUnlocked && (
				<div className='rounded-xl bg-[var(--tgui--bg_color,#111)] p-3 space-y-2'>
					<p className='text-xs text-[var(--tgui--hint_color,#999)]'>
						Backend dev procedures are locked. Enter password to unlock:
					</p>
					<div className='flex gap-2'>
						<input
							type='password'
							placeholder='Password'
							value={backendPassword}
							onChange={(e) => setBackendPassword(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && unlockBackend()}
							className='flex-1 rounded-lg bg-[var(--tgui--secondary_bg_color,#1c1c1e)] px-3 py-2 text-sm text-[var(--tgui--text_color,#fff)] outline-none placeholder:text-[var(--tgui--hint_color,#555)]'
						/>
						<button
							type='button'
							onClick={unlockBackend}
							disabled={loading === 'unlock' || !backendPassword}
							className='rounded-lg bg-[var(--tgui--link_color,rgb(41,144,255))] px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
						>
							{loading === 'unlock' ? '...' : 'Unlock'}
						</button>
					</div>
				</div>
			)}

			{/* ── Active account banner ───────────────────────────── */}
			<div className='rounded-xl bg-[var(--tgui--bg_color,#111)] p-3'>
				<div className='flex items-center justify-between'>
					<div>
						<p className='text-sm font-medium text-[var(--tgui--text_color,#fff)]'>
							{user.name || 'Unknown'}
						</p>
						<p className='text-xs text-[var(--tgui--hint_color,#999)]'>
							{user.role}
							{user.username ? ` · @${user.username}` : ''}
							{mode ? ` · ${mode}` : ''}
						</p>
					</div>
					<button
						type='button'
						onClick={resetToTelegram}
						className='rounded-lg bg-[var(--tgui--destructive_text_color,#ff453a)]/20 px-3 py-1.5 text-xs font-medium text-[var(--tgui--destructive_text_color,#ff453a)]'
					>
						Reset
					</button>
				</div>
			</div>

			{/* ── Preset quick users ─────────────────────────────── */}
			{backendUnlocked && (
				<div className='space-y-1.5'>
					<p className='text-xs font-medium text-[var(--tgui--hint_color,#999)]'>
						Quick switch
					</p>
					{PRESET_USERS.map((preset) => (
						<button
							key={preset.identifier}
							type='button'
							onClick={() => loginAs(preset.identifier)}
							disabled={loading !== null}
							className='flex w-full items-center justify-between rounded-xl bg-[var(--tgui--bg_color,#111)] px-3 py-2.5 text-left disabled:opacity-50'
						>
							<div>
								<p className='text-sm font-medium text-[var(--tgui--text_color,#fff)]'>
									{preset.label}
								</p>
								<p className='text-xs text-[var(--tgui--hint_color,#999)]'>
									{preset.description}
								</p>
							</div>
							{loading === preset.identifier && (
								<span className='text-xs text-[var(--tgui--hint_color,#999)]'>
									...
								</span>
							)}
						</button>
					))}
				</div>
			)}

			{/* ── Custom login input ─────────────────────────────── */}
			{backendUnlocked && (
				<div className='space-y-2'>
					<p className='text-xs font-medium text-[var(--tgui--hint_color,#999)]'>
						Custom login
					</p>
					<div className='flex gap-2'>
						<input
							type='text'
							placeholder='Telegram ID, ObjectId, or username'
							value={customIdentifier}
							onChange={(e) => setCustomIdentifier(e.target.value)}
							onKeyDown={(e) =>
								e.key === 'Enter' &&
								customIdentifier.trim() &&
								loginAs(customIdentifier.trim())
							}
							className='flex-1 rounded-lg bg-[var(--tgui--bg_color,#111)] px-3 py-2 text-sm text-[var(--tgui--text_color,#fff)] outline-none placeholder:text-[var(--tgui--hint_color,#555)]'
						/>
						<button
							type='button'
							onClick={() =>
								customIdentifier.trim() && loginAs(customIdentifier.trim())
							}
							disabled={loading !== null || !customIdentifier.trim()}
							className='rounded-lg bg-[var(--tgui--link_color,rgb(41,144,255))] px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
						>
							{loading === customIdentifier.trim() ? '...' : 'Login'}
						</button>
					</div>
				</div>
			)}

			{/* ── Error display ───────────────────────────────────── */}
			{error && (
				<p className='rounded-lg bg-[var(--tgui--destructive_text_color,#ff453a)]/10 px-3 py-2 text-xs text-[var(--tgui--destructive_text_color,#ff453a)]'>
					{error}
				</p>
			)}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasDevCookie(): boolean {
	return document.cookie.split(';').some((c) => {
		const [name, ...rest] = c.trim().split('=')
		return name === 'dev_mode' && rest.join('=').trim().length > 0
	})
}

/** `dev.Login` answers FORBIDDEN only from the dev-token middleware. */
function isDevTokenRejected(err: unknown): boolean {
	return (err as { data?: { code?: string } })?.data?.code === 'FORBIDDEN'
}

function clearDevCookie() {
	document.cookie = 'dev_mode=; Path=/; max-age=0; SameSite=Lax'
}
